import { useEffect,useRef } from 'react';
import { createPortal } from 'react-dom';

/**
 * A custom hook for creating portal element to a DOM element to render content outside the default tree.
 *
 * @param {string} rootId The element id that this portal refers to. Will be used to create the element if it doesn't exist yet.
 * @param {boolean} [prepend=false] Changes behavior to prepend, rather than append the element if so required.
 * @returns {Function} a Portal wrapper component that can be used to render content in the portal.
 */
const usePortal = ( rootId, prepend = false ) => {
  // Either gets the DOM element to be used (using rootId), or creates it when it doesn't exist yet.
  const el = useRef(document.getElementById(rootId) || document.createElement('div'));

  useEffect(() => {
    // Checks if element is created dynamically or if an existing element was used (to prevent accidently removing existing elements)
    const isDynamic = !el.current.parentElement;

    // capturing the current state of the element within the effect hook to prevent exhaustive-deps warning.
    const currentElement = el.current;

    // pre-/appends the element to the document body if the element was created by this component.
    if (isDynamic) {
      currentElement.id = rootId;
      if (prepend) {
        document.body.prepend(currentElement);
      } else {
        document.body.append(currentElement);
      }
    }

    // cleanup removes the element from document body if the element was appended by this component.
    return () => {
      if (isDynamic) {
        currentElement.parentElement.removeChild(currentElement);
      }
    };
  }, [rootId, prepend]);

  // Return the portal element
  return ({ children }) => createPortal(children, el.current);
};

export default usePortal;
